import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getTodayOverview } from "../../services/dashboardService";
import { Icon } from "@iconify/react";

const TodayOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const res = await getTodayOverview();
      if (res?.status && res?.data) {
        setData(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4 text-center text-muted">Loading Today's Overview...</div>;
  if (!data) return null;

  const { dashboard_tiles: tiles, attendance_monitoring: classes } = data;

  const statCards = [
    { label: "Total Students", value: tiles.total_students, icon: "mdi:account-group", color: "#4e73df" },
    { label: "Present", value: tiles.present_students, icon: "mdi:account-check", color: "#1cc88a" },
    { label: "Absent", value: tiles.absent_students, icon: "mdi:account-remove", color: "#e74a3b" },
    { label: "Leave", value: tiles.leave_students || tiles.leave || 0, icon: "mdi:account-minus", color: "#7e22ce" },
    { label: "Late Arrivals", value: tiles.late_arrivals, icon: "mdi:clock-alert", color: "#f6c23e" },
    { label: "Attendance %", value: tiles.attendance_percentage, icon: "mdi:chart-pie", color: "#36b9cc" }
  ];

  const filteredClasses = classes?.filter(cls => 
    cls.class_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalPages = Math.ceil(filteredClasses.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentClasses = filteredClasses.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <div className="today-overview-scope mt-4">
      <style>{`
        .today-overview-scope .stat-card {
          transition: transform 0.2s, box-shadow 0.2s;
          border-radius: 12px;
          border: 1px solid #e3e6f0;
          background: #fff;
        }
        .today-overview-scope .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
        }
        .today-overview-scope .icon-wrapper {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .today-overview-scope .stat-label {
          color: #858796;
          font-size: 0.8rem;
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .today-overview-scope .stat-value {
          font-size: 1.5rem;
          font-weight: 800;
          color: #5a5c69;
        }
        .today-overview-scope .premium-table-card {
          border-radius: 12px;
          border: 1px solid #e3e6f0;
          overflow: hidden;
        }
        .today-overview-scope .premium-table thead th {
          background-color: #f8f9fc;
          color: #4e73df;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e3e6f0;
          padding: 1rem;
        }
        .today-overview-scope .premium-table tbody td {
          vertical-align: middle;
          padding: 1rem;
          color: #5a5c69;
          font-weight: 600;
          border-bottom: 1px solid #f1f3f9;
        }
        .today-overview-scope .premium-table tbody tr:hover {
          background-color: #f8f9fc;
        }
        .today-overview-scope .search-box-premium {
          border-radius: 50rem;
          background: #f8f9fc;
          border: 1px solid #e3e6f0;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .today-overview-scope .search-box-premium input {
          border: none;
          background: transparent;
          outline: none;
          width: 200px;
          font-size: 0.85rem;
        }
        .today-overview-scope .pagination-btn {
          background: #fff;
          border: 1px solid #e3e6f0;
          color: #4e73df;
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .today-overview-scope .pagination-btn:hover:not(:disabled) {
          background: #f8f9fc;
        }
        .today-overview-scope .pagination-btn.active {
          background: #4e73df;
          color: #fff;
          border-color: #4e73df;
        }
        .today-overview-scope .pagination-btn:disabled {
          color: #858796;
          cursor: not-allowed;
          opacity: 0.6;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
          <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
          Today's Attendance Summary ({data.date})
        </h6>

      
        <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle shadow-sm" onClick={fetchOverview} style={{ width: '40px', height: '40px' }} title="Refresh Dashboard">
          <Icon icon="mdi:refresh" className="text-secondary" fontSize="20px" />
        </button>
      </div>
      
      {/* Stat Cards */}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-6 gy-4 mb-4">
        {statCards.map((stat, i) => (
          <div className="col" key={i}>
            <div className="card stat-card shadow-sm h-100" style={{ borderLeft: `4px solid ${stat.color}` }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="stat-label">{stat.label}</p>
                    <h6 className="stat-value mb-0">{stat.value}</h6>
                  </div>
                  <div className="icon-wrapper" style={{ backgroundColor: `${stat.color}20` }}>
                    <Icon icon={stat.icon} style={{ color: stat.color, fontSize: "24px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Class-wise Monitoring */}
      <div className="card shadow-sm premium-table-card">
        <div className="card-header bg-white py-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
          <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
            <div style={{ width: '6px', height: '18px', backgroundColor: '#06b6d4', borderRadius: '3px' }}></div>
            Class-wise Attendance Monitoring
          </h6>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', borderRadius: '50rem', backgroundColor: '#f8f9fc', borderColor: '#e3e6f0' }}
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5 Rows</option>
              <option value={10}>10 Rows</option>
              <option value={25}>25 Rows</option>
              <option value={50}>50 Rows</option>
              <option value={100}>100 Rows</option>
            </select>
            <div className="search-box-premium">
              <Icon icon="mdi:magnify" className="text-muted" fontSize="18px" />
              <input 
                type="text" 
                placeholder="Search by Class Name..." 
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table premium-table mb-0">
              <thead>
                <tr>
                  <th>Class</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Present</th>
                  <th className="text-center">Absent</th>
                  <th className="text-center">Late</th>
                  <th className="text-center">Leave</th>
                  <th className="text-center">Not Marked</th>
                  <th className="text-center">Attendance %</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentClasses.length > 0 ? (
                  currentClasses.map((cls, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold text-dark border-end-0">{cls.class_name}</td>
                      <td className="text-center">{cls.total_students}</td>
                      <td className="text-center text-success">{cls.present_students}</td>
                      <td className="text-center text-danger">{cls.absent_students}</td>
                      <td className="text-center text-warning">{cls.late_arrivals}</td>
                      <td className="text-center" style={{ color: "#7e22ce" }}>{cls.leave_students || cls.leave || 0}</td>
                      <td className="text-center text-secondary">{cls.not_marked}</td>
                      <td className="text-center">
                        <span className="badge px-3 py-2" style={{ backgroundColor: '#4e73df' }}>
                          {cls.attendance_percentage}
                        </span>
                      </td>
                      <td className="text-center">
                        <Link to={`/attendance/class/${cls.class_id}`} className="btn btn-sm btn-outline-primary rounded-pill px-3 d-inline-flex align-items-center gap-1">
                          <Icon icon="mdi:eye-outline" /> View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      <Icon icon="mdi:flask-empty-outline" style={{ fontSize: '40px', marginBottom: '10px', opacity: 0.5 }} />
                      <br/>
                      No class data found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-3">
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredClasses.length)} of {filteredClasses.length} entries
            </span>
            <div className="d-flex rounded overflow-hidden">
              <button 
                className="pagination-btn rounded-start"
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i} 
                  className={`pagination-btn border-start-0 border-end-0 ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="pagination-btn rounded-end"
                disabled={currentPage === totalPages} 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayOverview;
